import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TokenPair } from '../client/auth-store';
import { newIdempotencyKey } from '../client/idempotency';
import { ApplyPromo, ChangeCheckoutPlan, CheckoutSession, PlanCatalogue, } from './contracts';
/**
 * The funnel's data layer.
 *
 * Every route here is `@Public()` on the backend — "nobody has an account yet,
 * which is the point" — so none of these carry a bearer token, and the client
 * used is the unauthenticated one.
 *
 * **The checkout session id is the capability.** Holding it is what lets a
 * browser read and advance its own signup, so it is a v4 UUID, it never appears
 * in a listing, and we keep it out of logs.
 */
/**
 * The plan catalogue for the landing page.
 *
 * `/plans/catalogue`, not `/plans`. The latter is authenticated and carries the
 * full price history and Stripe ids; this one is `@Public()` and carries only
 * what a new subscriber would be charged.
 */
export function usePlanCatalogue(http, audience, locale) {
    return useQuery({
        queryKey: ['plans', 'catalogue', audience, locale],
        queryFn: () => http.get('/plans/catalogue', {
            // Filtered server-side. The string we filter by is the string we post
            // as `StartCheckout.path`.
            query: { audience },
            schema: PlanCatalogue,
            locale,
        }),
        // Prices change rarely and the landing page is the top of a paid funnel.
        staleTime: 10 * 60_000,
    });
}
/**
 * Poll a session.
 *
 * `refetchInterval` is the "setting up your account" state: activation is
 * asynchronous and driven by Stripe's webhook, so the funnel watches rather
 * than assuming. It stops once the session reaches a terminal state — polling
 * an `ACTIVE` session forever is a request every three seconds for nothing.
 */
export function useCheckoutSession(http, id) {
    return useQuery({
        queryKey: ['checkout', id],
        enabled: Boolean(id),
        queryFn: () => http.get(`/checkout/${id}`, { schema: CheckoutSession }),
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            if (!status)
                return false;
            return status === 'PENDING_ACTIVATION' || status === 'AWAITING_PAYMENT' ? 3000 : false;
        },
        /*
         * Keep polling while the tab is in the background.
         *
         * TanStack Query suspends `refetchInterval` when the window loses focus,
         * which is right almost everywhere and wrong here: the single most likely
         * thing a member does on "we are setting up your account" is switch to
         * their email to look for the receipt. Coming back to a page still saying
         * "setting up" — when activation finished ninety seconds ago — reads as a
         * stuck signup.
         *
         * Safe to leave on because it is bounded: the interval returns `false` the
         * moment the session reaches a terminal state, so this polls for seconds,
         * not for the life of the tab.
         */
        refetchIntervalInBackground: true,
    });
}
/**
 * Start a signup.
 *
 * One transaction server-side: "an account with no session, or an organisation
 * with no account, leaves someone unable to finish AND unable to start again,
 * because their email is now taken."
 */
export function useStartCheckout(http) {
    return useMutation({
        mutationFn: (input) => http.post('/checkout', input, {
            schema: CheckoutSession,
            // One key per attempt. A double-tap on a flaky phone connection is the
            // normal case at the end of a signup form, not the edge case.
            idempotencyKey: newIdempotencyKey(),
        }),
    });
}
/**
 * Every mutation here returns the WHOLE updated session, so each one writes it
 * straight into the query cache.
 *
 * Without that the screen waits for the next poll to notice a change it already
 * has in hand — which read as "the Pay button does nothing" for up to three
 * seconds, and would have been worse on the real API where the poll is the only
 * other source of truth. Refetching instead would be a second round trip for a
 * response we are already holding.
 */
export function useApplyPromo(http, id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (code) => http.post(`/checkout/${id}/promo`, ApplyPromo.parse({ code }), {
            schema: CheckoutSession,
        }),
        onSuccess: (session) => queryClient.setQueryData(['checkout', id], session),
    });
}
/**
 * Change the plan on an in-progress checkout — the Confirm-plan tier switcher.
 *
 * Faking this with `start` collides with EMAIL_ALREADY_REGISTERED, so the
 * server owns the switch on the existing session and re-quotes through the
 * same `quoteCheckout` every other path uses. Only allowed while DRAFT.
 */
export function useChangeCheckoutPlan(http, id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (planKey) => http.post(`/checkout/${id}/change-plan`, ChangeCheckoutPlan.parse({ planKey }), {
            schema: CheckoutSession,
        }),
        onSuccess: (session) => queryClient.setQueryData(['checkout', id], session),
    });
}
/**
 * Ask Stripe for a hosted Checkout page.
 *
 * The response carries `paymentUrl` and the browser is redirected to it. The
 * prototype's branded two-column card form exists to specify the branding, not
 * to be rebuilt — card details never touch this origin.
 */
export function usePayCheckout(http, id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (urls) => http.post(`/checkout/${id}/pay`, urls, {
            schema: CheckoutSession,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: (session) => queryClient.setQueryData(['checkout', id], session),
    });
}
/**
 * Turn an activated checkout into an authenticated session — the Welcome
 * screen's "Enter" button after the Stripe callback.
 *
 * Client-reported bug the endpoint exists to fix: the account activated but
 * the browser never had a session, so clicking Enter navigated to `/` and
 * `IndexScreen`'s `!viewer` branch re-rendered the funnel. Now Enter POSTs
 * `/checkout/{id}/enter`, the server mints tokens for the linked user, and
 * the client stores them via the caller's auth setter before the reload.
 *
 * The mutation only knows how to fetch — the caller wires `onSuccess` to
 * store tokens because the AuthStore lives one layer up (`lib/client.ts`)
 * and is deliberately not passed through the domain layer.
 */
export function useEnterCheckout(http, id) {
    return useMutation({
        mutationFn: () => http.post(`/checkout/${id}/enter`, undefined, {
            schema: TokenPair,
        }),
    });
}
//# sourceMappingURL=hooks.js.map